import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslationForArray } from '@monorepo/client-common';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  Box,
  Stack,
  Typography,
  useBreakpoints,
  useTheme,
} from '@monorepo/nova-ui';
import { useHeaderMenuLinks } from '../../../api/queries/use-header-menu-queries';
import { useMenuTranslationsQuery } from '../../../api/queries/use-menu-translations-query';
import { useConditionalLink, useGetEnvironment } from '../../../hooks';
import { MenuBanner } from './components';

export const SubMenuMobile = ({
  handleCloseSubMenu,
}: {
  handleCloseSubMenu: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: _headerMenuLinks } = useHeaderMenuLinks();
  const { data: menuTranslations } = useMenuTranslationsQuery();
  const i18nEntries = useMemo(
    () => menuTranslations?.['headerMenuLinks'] || [],
    [menuTranslations],
  );
  const { is5ers } = useGetEnvironment();
  const [expandedMenuIds, setExpandedMenuIds] = useState<Set<string>>(
    new Set(['1']),
  ); // Default expand Overview
  const [lastExpandedMenuId, setLastExpandedMenuId] = useState<string | null>(
    '1',
  );
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const { isDesktop } = useBreakpoints();

  const Link = useConditionalLink();

  const toggleMenuExpansion = (menuId: string) => {
    setExpandedMenuIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);

        if (menuId === lastExpandedMenuId) {
          setLastExpandedMenuId(
            newSet.size > 0 ? Array.from(newSet)[newSet.size - 1] : null,
          );
        }
      } else {
        newSet.add(menuId);
        setLastExpandedMenuId(menuId);
      }
      return newSet;
    });
  };

  const onHover = (id: string) => {
    setHoveredItemId(id);
  };

  return (
    <Stack
      direction="column"
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        padding: theme.typography.pxToRem(16),
      }}
    >
      {/* Menu Content */}
      <Stack direction="column" sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {_headerMenuLinks?.map((menu, idx, arr) => {
          const isExpanded = expandedMenuIds.has(menu.id);
          const translatedMenuLabel = getDynamicTranslationForArray(
            menu.label,
            i18nEntries,
            menu.id,
            'label',
            t,
          );

          return menu.active ? (
            <Box key={menu.id}>
              {/* Main Menu Item */}
              <Box
                onClick={() => toggleMenuExpansion(menu.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 4,
                  cursor: 'pointer',
                  borderBottom:
                    idx === arr.length - 1
                      ? 'none'
                      : `${theme.typography.pxToRem(1)} solid ${theme.palette.background.contrast}`,
                  backgroundColor: isExpanded
                    ? theme.palette.background.contrast
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isExpanded
                      ? theme.palette.background.contrast
                      : isDesktop
                        ? theme.palette.background.contrast
                        : 'transparent',
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: theme.typography.pxToRem(14),
                    fontWeight: isExpanded ? 600 : 400,
                    color: isExpanded
                      ? theme.palette.primary.main
                      : theme.palette.text.main,
                  }}
                >
                  {translatedMenuLabel}
                </Typography>
                <SystemIcon
                  iconName={isExpanded ? 'CaretDown' : 'CaretRight'}
                  sx={{
                    fontSize: 20,
                    color: theme.palette.text.third,
                  }}
                />
              </Box>

              {/* Submenu Items */}
              {isExpanded && menu.children && (
                <Box>
                  {menu.children
                    .filter((child) => child.active !== false)
                    .map((child, idx, arr) => {
                      const translatedChildLabel =
                        getDynamicTranslationForArray(
                          child.label,
                          i18nEntries,
                          child.id,
                          'label',
                          t,
                        );

                      return (
                        <Link
                          href={child.href ?? ''}
                          key={child.id}
                          onMouseEnter={() => onHover(child.id)}
                          onMouseLeave={() => onHover('')}
                          style={{ textDecoration: 'none' }}
                          nextjs={child.nextjs}
                          {...(child.openInNewTab && {
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          })}
                          onClick={handleCloseSubMenu}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 4,
                              ml: 8,
                              borderBottom:
                                idx === arr.length - 1
                                  ? 'none'
                                  : `${theme.typography.pxToRem(1)} solid ${theme.palette.background.contrast}`,
                              backgroundColor:
                                hoveredItemId === child.id
                                  ? theme.palette.background.contrast
                                  : 'transparent',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.background.contrast,
                              },
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: theme.typography.pxToRem(14),
                                fontWeight: 400,
                                color: theme.palette.text.main,
                              }}
                            >
                              {translatedChildLabel}
                            </Typography>
                            <SystemIcon
                              iconName="ArrowRightMenu"
                              color={theme.palette.text.third}
                              viewBox="0 0 34 34"
                              size={34}
                            />
                          </Box>
                        </Link>
                      );
                    })}
                </Box>
              )}
            </Box>
          ) : (
            <Link
              href={menu.href ?? ''}
              key={menu.id}
              onClick={handleCloseSubMenu}
              style={{ textDecoration: 'none' }}
              {...(menu.openInNewTab && {
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
            >
              <Typography
                variant="body1"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 4,
                  cursor: 'pointer',
                  borderBottom: `${theme.typography.pxToRem(1)} solid ${theme.palette.background.contrast}`,
                  backgroundColor: isExpanded
                    ? theme.palette.background.contrast
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isExpanded
                      ? theme.palette.background.contrast
                      : isDesktop
                        ? theme.palette.background.contrast
                        : 'transparent',
                  },
                }}
              >
                {translatedMenuLabel}
              </Typography>
            </Link>
          );
        })}
        {is5ers && lastExpandedMenuId && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <MenuBanner
              activeMenu={
                _headerMenuLinks?.find(
                  (menu) => menu.id === lastExpandedMenuId,
                ) || null
              }
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
